import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { apiResponse, apiError } from '../schemas/common.schema';

const c = initContract();
const dateish = z.union([z.string(), z.date()]);

export const ArticleResponseSchema = z.object({
  _id: z.string(),
  article_slug: z.string().optional(),
  article_title: z.string().optional(),
  article_created_at: dateish.optional(),
  article_modified_at: dateish.optional(),
  article_image: z.string().optional(),
  article_content: z.any().optional(),
  article_author: z.any().optional(),
  article_views: z.number().optional(),
  article_related_course_ids: z.array(z.string()).optional(),
  article_locale: z.string().optional(),
  article_seo_keywords: z.array(z.string()).optional(),
});

export const getArticlesQuery = z.object({
  cursor: z.string().optional(),
  limit: z.coerce.number().optional(),
  article_locale: z.string().optional(),
  sort_by: z.enum(['created_at', 'modified_at', 'title']).optional(),
  sort_order: z.enum(['asc', 'desc']).optional(),
  q: z.string().optional(),
  date_from: z.string().optional(),
  date_to: z.string().optional(),
});

export const createArticleBody = z.object({
  article_title: z.string(),
  article_slug: z.string().optional(),
  article_image: z.string().optional(),
  article_content: z.any().optional(),
  article_author: z.any(),
  article_related_course_ids: z.array(z.string()).optional(),
  article_locale: z.string(),
  article_seo_keywords: z.array(z.string()).optional(),
});

export const updateArticleBody = createArticleBody.partial();

// Bare responses (single entity): the API returns the entity directly, not enveloped.
const articleResp = ArticleResponseSchema;
const viewsResp = z.object({ article_views: z.number() });
const deletedResp = z.object({ deleted: z.boolean() });

export const articleRoutes = {
  getArticles: {
    method: 'GET',
    query: getArticlesQuery,
    responses: { 200: apiResponse(z.array(ArticleResponseSchema)) },
  },
  getArticleBySlug: {
    method: 'GET',
    pathParams: z.object({ slug: z.string() }),
    responses: { 200: articleResp, 404: apiError },
  },
  getArticleById: {
    method: 'GET',
    pathParams: z.object({ article_id: z.string() }),
    responses: { 200: articleResp, 404: apiError },
  },
  createArticle: {
    method: 'POST',
    body: createArticleBody,
    responses: { 200: articleResp },
  },
  updateArticle: {
    method: 'PATCH',
    pathParams: z.object({ article_id: z.string() }),
    body: updateArticleBody,
    responses: { 200: articleResp, 404: apiError },
  },
  incrementArticleView: {
    method: 'POST',
    pathParams: z.object({ article_id: z.string() }),
    body: z.object({}).optional(),
    responses: { 200: viewsResp, 404: apiError },
  },
  deleteArticle: {
    method: 'DELETE',
    pathParams: z.object({ article_id: z.string() }),
    responses: { 200: deletedResp, 404: apiError },
  },
} as const;

export const articlesContract = c.router({
  getArticles: { ...articleRoutes.getArticles, path: '/articles' },
  getArticleBySlug: { ...articleRoutes.getArticleBySlug, path: '/articles/details/:slug' },
  getArticleById: { ...articleRoutes.getArticleById, path: '/articles/:article_id' },
  createArticle: { ...articleRoutes.createArticle, path: '/articles' },
  updateArticle: { ...articleRoutes.updateArticle, path: '/articles/:article_id' },
  incrementArticleView: {
    ...articleRoutes.incrementArticleView,
    path: '/articles/:article_id/increment-view',
  },
  deleteArticle: { ...articleRoutes.deleteArticle, path: '/articles/:article_id' },
});

export type ArticleResponseDto = z.infer<typeof ArticleResponseSchema>;
